import { type FinancesFilters } from '../../../../types/finances';

export interface ColumnVisibility {
  dateColumn: boolean;
  sspColumn: boolean;
  dspColumn: boolean;
  dynamicGroupByColumns: string[];
  extraColsColumns: string[];
  hiddenByResponse: Set<string>;
  totalColumns: number;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getColumnVisibility = (filters: FinancesFilters, rows: any[] = []): ColumnVisibility => {
  const groupByArray = filters.groupBy || [];
  const extraColsArray = filters.extraCols || [];

  // Date column visible when format is not summary
  const dateColumn = filters.format !== 'summary';

  // SSP column visible when:
  // 1. sspId is in groupBy array, OR
  // 2. No SSP filter is applied AND no groupBy is selected
  const sspColumn =
    groupByArray.includes('sspId') || ((!filters.sspId || filters.sspId.length === 0) && groupByArray.length === 0);

  // DSP column visible when:
  // 1. dspId is in groupBy array, OR
  // 2. No DSP filter is applied AND no groupBy is selected
  const dspColumn =
    groupByArray.includes('dspId') || ((!filters.dspId || filters.dspId.length === 0) && groupByArray.length === 0);

  // Dynamic columns for non-standard groupBy fields
  const dynamicGroupByColumns = groupByArray.filter((field) => !['sspId', 'dspId'].includes(field));
  const extraColsColumns = extraColsArray;

  // Build a set of keys not present in any response row
  const hiddenByResponse = new Set<string>();
  if (rows && rows.length > 0) {
    const presentKeys = new Set<string>();
    for (const row of rows) {
      for (const key of Object.keys(row)) {
        presentKeys.add(key);
      }
    }
    // Check named columns
    const namedColumns: Record<string, boolean> = {
      datetime: dateColumn,
      sspId: sspColumn,
      dspId: dspColumn,
    };
    for (const [key] of Object.entries(namedColumns)) {
      if (!presentKeys.has(key)) {
        hiddenByResponse.add(key);
      }
    }
    // Check metric columns
    const metricKeys = [
      'sspRequests',
      'dspRequests',
      'coverage',
      'winRate',
      'bids',
      'impressions',
      'nurl',
      'burl',
      'sspSpend',
      'dspSpend',
      'profit',
    ];
    for (const key of metricKeys) {
      if (!presentKeys.has(key)) {
        hiddenByResponse.add(key);
      }
    }
    // Check dynamic groupBy columns
    for (const field of dynamicGroupByColumns) {
      if (!presentKeys.has(field)) {
        hiddenByResponse.add(field);
      }
    }
    // Check extra cols columns
    for (const field of extraColsColumns) {
      if (!presentKeys.has(field)) {
        hiddenByResponse.add(field);
      }
    }
  }

  // Calculate total columns
  let totalColumns = 1; // Always have at least the "Total" column in summary
  if (dateColumn && !hiddenByResponse.has('datetime')) totalColumns++;
  if (sspColumn && !hiddenByResponse.has('sspId')) totalColumns++;
  if (dspColumn && !hiddenByResponse.has('dspId')) totalColumns++;
  totalColumns += dynamicGroupByColumns.filter((f) => !hiddenByResponse.has(f)).length;
  totalColumns += extraColsColumns.filter((f) => !hiddenByResponse.has(f)).length;
  const coreMetricKeys = [
    'sspRequests',
    'dspRequests',
    'coverage',
    'winRate',
    'bids',
    'impressions',
    'nurl',
    'burl',
    'sspSpend',
    'dspSpend',
    'profit',
  ];
  totalColumns += coreMetricKeys.filter((k) => !hiddenByResponse.has(k)).length;

  return {
    dateColumn,
    sspColumn,
    dspColumn,
    dynamicGroupByColumns,
    extraColsColumns,
    hiddenByResponse,
    totalColumns,
  };
};
